import { auth, db } from '../../config/main_config';
import { createToast } from './createToast.util';
import { closeModal } from './closeModal.util';
import { waitForCompanyID } from './waitForCompanyID.util';
import { window } from './window.util';
import { Issue } from '../../../../interfaces/issue.interface';

// Helper function to get the current date and time in the required format
function getFormattedTimestamp(): string {
    const now = new Date();
    const options: Intl.DateTimeFormatOptions = {
        hour: 'numeric',
        minute: 'numeric',
        second: 'numeric',
        hour12: true
    };
    const timeString = now.toLocaleString('en-US', options).replace(/\s/g, ''); // 'HH:MM:SS_AM/PM'

    const day = String(now.getDate()).padStart(2, '0');
    const month = String(now.getMonth() + 1).padStart(2, '0'); // Month is zero-based
    const year = now.getFullYear();

    return `${day}_${month}_${year}@${timeString}`;
}

// Function to save the current register to the history subcollection before adding/editing an issue
async function updateHistoryCollection(companyID: string, currentRegister: Record<string, Issue>): Promise<void> {
    const timestamp = getFormattedTimestamp();
    const historyRef = db.collection(`/customers/${companyID}/product/managementSystemGuide/issueRegister/register/history`).doc(timestamp);

    try {
        await historyRef.set(currentRegister);
        console.log(`Register snapshot saved to history as ${timestamp}`);
    } catch (error) {
        console.error('Error saving register snapshot to history:', error);
    }
}

// Function to delete an issue
async function deleteIssue(companyID: string, issueID: string): Promise<void> {
    const documentRef = db.doc(`/customers/${companyID}/product/managementSystemGuide/issueRegister/register`);

    try {
        // Fetch the current state of the register
        const docSnapshot = await documentRef.get();
        let currentRegister: Record<string, Issue> = {};

        if (docSnapshot.exists) {
            currentRegister = docSnapshot.data() as Record<string, Issue>;

            // Get the issue to be deleted
            const deletedIssue = currentRegister[issueID];

            // Save the current register to the history subcollection before deleting the issue
            await updateHistoryCollection(companyID, currentRegister);

            // Delete the issue from the register
            delete currentRegister[issueID];

            // Update the register in Firestore
            await documentRef.set(currentRegister);

            console.log('Issue deleted successfully:', issueID);

            // If the issue has an origin from the old document, update it there as well
            if (deletedIssue && deletedIssue.origin && deletedIssue.origin.oldDocument) {
                const oldDocumentRef = db.doc(`/customers/${companyID}/product/policyPackages/ISO27001keyDocuments/ismsManual`);
                const oldDocSnapshot = await oldDocumentRef.get();

                if (oldDocSnapshot.exists) {
                    const oldData = oldDocSnapshot.data() || {};
                    const mapName = deletedIssue.origin.map; // 'issuesExternal' or 'issuesInternal'
                    const issueKey = deletedIssue.origin.issueKey;

                    if (oldData[mapName] && oldData[mapName][issueKey]) {
                        // Remove the issue from the old document
                        delete oldData[mapName][issueKey];

                        // Save the updated old document
                        await oldDocumentRef.set(oldData);

                        console.log(`Issue deleted from old document at ${mapName}/${issueKey}`);
                    } else {
                        console.warn('Issue not found in the old document.');
                    }
                } else {
                    console.error('Old document not found.');
                }
            }
        } else {
            console.error('No register found for the given company ID.');
        }
    } catch (error) {
        console.error('Error deleting issue:', error);
    }
}

// Function to handle delete issue button click
export async function deleteIssueHandler(event: Event, issueID: string): Promise<void> {
    event.preventDefault();

    const companyID = await waitForCompanyID(); // Assume this function fetches the current company ID

    if (companyID && issueID) {
        // Confirm deletion with the user
        const confirmation = confirm('Are you sure you want to delete this issue? This action cannot be undone.');
        if (confirmation) {
            await deleteIssue(companyID, issueID);
            createToast({
                message: 'Issue deleted successfully.'
            });
            // Optionally, refresh the issue register or remove the deleted issue from the UI
            // For example, remove the row from the table
            const issueRow = document.querySelector(`tr.tableordion-row[data-issue-id="${issueID}"]`);
            const issueContentRow = document.querySelector(`tr.tableordion-content[data-issue-id="${issueID}"]`);
            if (issueRow) {
                issueRow.remove();
            }
            if (issueContentRow) {
                issueContentRow.remove();
            }
        }
    } else {
        createToast({
            message: 'An error occurred when trying to delete the issue.',
            customStyles: {
                backgroundColor: '#ff0000',
                color: '#ffffff'
            }
        });
        return;
    }
}

// Make deleteIssueHandler available globally
(window as any).deleteIssueHandler = deleteIssueHandler;

// Function to edit an existing issue
async function editIssue(companyID: string, issueID: string, origin: object | null = null): Promise<void> {
    const dateAddedEditElement = document.getElementById('dateAdded_edit') as HTMLInputElement;
    const issueTypeElement = document.getElementById('issueType_edit') as HTMLInputElement;
    const typeElement = document.getElementById('type_edit') as HTMLInputElement;
    const nameElement = document.getElementById('name_edit') as HTMLInputElement;
    const descriptionElement = document.getElementById('description_edit') as HTMLInputElement;
    const risksElement = document.getElementById('risksSelected_edit') as HTMLInputElement;
    const objectivesElement = document.getElementById('objectivesSelected_edit') as HTMLInputElement;
    const legalObligationsElement = document.getElementById('legalObligationsSelected_edit') as HTMLInputElement;
    const frameworksElement = document.getElementById('frameworksSelected_edit') as HTMLInputElement;

    // Ensure all elements are found
    if (!dateAddedEditElement || !issueTypeElement || !typeElement || !nameElement || !descriptionElement ||
        !risksElement || !objectivesElement || !legalObligationsElement || !frameworksElement) {
        console.error('One or more input elements not found.');
        return;
    }

    const dateEdited = dateAddedEditElement.value;
    const issueType = issueTypeElement.value;
    const type = typeElement.value;
    const name = nameElement.value;
    const description = descriptionElement.value;
    const risks = window.selectedRisks || risksElement.value.split(', ');
    const objectives = window.selectedISMSObjectives || objectivesElement.value.split(', ');
    const legalObligations = window.selectedLegalObligations || legalObligationsElement.value.split(', ');
    const frameworks = window.selectedControls || frameworksElement.value.split(', ');

    const documentRef = db.doc(`/customers/${companyID}/product/managementSystemGuide/issueRegister/register`);

    try {
        // Fetch the current state of the register
        const docSnapshot = await documentRef.get();
        let currentRegister: Record<string, Issue> = {};

        if (docSnapshot.exists) {
            currentRegister = docSnapshot.data() as Record<string, Issue>;

            // Save the current register to the history subcollection before editing the issue
            await updateHistoryCollection(companyID, currentRegister);

            // Update the issue with the new data
            const updatedIssue: Issue = {
                id: issueID,
                name: name,
                description: description,
                type: type,
                dateAdded: dateEdited, // Date of the edit
                details: {
                    issueType: issueType,
                    risks: risks,
                    objectives: objectives,
                    legalObligations: legalObligations,
                    frameworks: frameworks
                }
            };

            // Include the origin field if it exists in the original issue
            const originalIssue = window.issueRegister.find(issue => issue.id === issueID);
            if (originalIssue && originalIssue.origin) {
                updatedIssue.origin = originalIssue.origin;
                console.log('Converted issue to new format:', updatedIssue);
            }

            // Update the issue in the register
            await documentRef.update({
                [issueID]: updatedIssue
            });

            console.log('Issue edited successfully:', updatedIssue);

            // If the issue has an origin from the old document, update it there as well
            if (updatedIssue.origin && updatedIssue.origin.oldDocument) {
                const oldDocumentRef = db.doc(`/customers/${companyID}/product/policyPackages/ISO27001keyDocuments/ismsManual`);
                const oldDocSnapshot = await oldDocumentRef.get();

                if (oldDocSnapshot.exists) {
                    const oldData = oldDocSnapshot.data() || {};
                    const mapName = updatedIssue.origin.map; // 'issuesExternal' or 'issuesInternal'
                    const issueKey = updatedIssue.origin.issueKey;

                    if (!oldData[mapName]) {
                        oldData[mapName] = {};
                    }

                    // Update the issue in the appropriate map
                    oldData[mapName][issueKey] = {
                        dateAdded: dateEdited,
                        dateMovedToMSG: new Date(), // You may want to handle this appropriately
                        issue: name,
                        updatedBy: auth.currentUser.uid, // You may want to include who updated it
                        description: updatedIssue.description,
                        type: updatedIssue.type,
                        details: updatedIssue.details,
                    };

                    // Save the updated old document
                    await oldDocumentRef.set(oldData);

                    console.log(`Issue updated in old document at ${mapName}/${issueKey}`);
                } else {
                    console.error('Old document not found.');
                }
            }
        } else {
            console.error('No register found for the given company ID.');
        }
    } catch (error) {
        console.error('Error editing issue:', error);
    }
}

// Function to retrieve issue data
export async function getIssueData(companyID: string, issueID: string): Promise<Issue | null> {
    // Check if window.issueRegister exists before trying to fetch data
    if (window.issueRegister) {
        for (const issue of window.issueRegister) {
            if (issue.id === issueID) {
                return issue;
            }
        }

        console.error(`No issue found with ID ${issueID} in window.issueRegister`);
        return null;
    } else {
        console.warn('No issue register data found in the window. Checking the database.');
        const documentRef = db.doc(`/customers/${companyID}/product/managementSystemGuide/issueRegister/register`);

        const docSnapshot = await documentRef.get();

        if (docSnapshot.exists) {
            const data = docSnapshot.data();

            if (data && data[issueID]) {
                return data[issueID] as Issue;
            } else {
                console.error(`No issue found with ID ${issueID}`);
                return null;
            }
        } else {
            console.log("No document found at the specified path.");
            return null;
        }
    }
}

// Triggered when the "Save" button in the edit modal is clicked
export async function saveEditedIssue(event: Event, issueID: string | null = null): Promise<void> {
    event.preventDefault();

    const companyID = await waitForCompanyID(); // Assume this function fetches the current company ID

    if (issueID === null) {
        const editModal = document.querySelector("[data-managedid='EditNewIssueModal'].show") as HTMLElement;
        if (editModal) {
            issueID = editModal.dataset.currentIssueId || null;
        } else {
            console.error('No issue ID provided in the modal data-current-issue-id attribute');
            return;
        }
    }

    if (companyID && issueID) {
        await editIssue(companyID, issueID);
        closeModal(null, true); // Close the modal after saving
        createToast({
            message: 'Issue edited successfully.'
        });
    } else {
        createToast({
            message: 'An error occurred when trying to save your changes.',
            customStyles: {
                backgroundColor: '#ff0000',
                color: '#ffffff'
            }
        });
        return;
    }
}